import { useState, useMemo } from 'react';
import { useMetrics, type DateRange } from '../hooks/useApi';

type Preset = 'week' | 'last_week' | '30days' | 'custom';

function getPresetRange(preset: Preset): DateRange {
  const now = new Date();
  const fmt = (d: Date) => d.toISOString().slice(0, 10);

  switch (preset) {
    case 'week': {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      return { from: fmt(start), to: fmt(now) };
    }
    case 'last_week': {
      const end = new Date(now);
      end.setDate(now.getDate() - now.getDay() - 1);
      const start = new Date(end);
      start.setDate(end.getDate() - 6);
      return { from: fmt(start), to: fmt(end) };
    }
    case '30days': {
      const start = new Date(now);
      start.setDate(now.getDate() - 30);
      return { from: fmt(start), to: fmt(now) };
    }
    default:
      return {};
  }
}

const CATEGORY_COLORS: Record<string, string> = {
  CUSTOM_ORDER: 'bg-blue-500',
  PROD_STATUS: 'bg-green-500',
  SHIPPING: 'bg-yellow-500',
  PRODUCT_SUPPORT: 'bg-teal-500',
  WHOLESALE: 'bg-purple-500',
  FEEDBACK: 'bg-orange-500',
  BOOKING: 'bg-cyan-500',
  INTERNAL: 'bg-gray-400',
};

export default function MetricsView() {
  const [preset, setPreset] = useState<Preset>('week');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const dateRange = useMemo(() => {
    if (preset === 'custom') return { from: customFrom || undefined, to: customTo || undefined };
    return getPresetRange(preset);
  }, [preset, customFrom, customTo]);

  const { data: metrics, isLoading, isError } = useMetrics(dateRange);

  const maxCategoryCount = useMemo(() => {
    if (!metrics?.by_category) return 1;
    return Math.max(1, ...Object.values(metrics.by_category).map((c) => c.count));
  }, [metrics]);

  const statCards = [
    {
      label: 'Total Received',
      value: metrics?.total_received ?? '-',
      color: 'text-blue-600',
    },
    {
      label: 'Auto-Handled',
      value: metrics ? `${metrics.auto_handled_pct.toFixed(1)}%` : '-',
      color: 'text-emerald-600',
    },
    {
      label: 'Avg Response Time',
      value: metrics ? `${metrics.avg_response_time_min.toFixed(0)} min` : '-',
      color: 'text-amber-600',
    },
    {
      label: 'Accuracy',
      value: metrics ? `${metrics.accuracy_pct.toFixed(1)}%` : '-',
      color: 'text-purple-600',
    },
  ];

  return (
    <div>
      {/* Date range selector */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {(
          [
            ['week', 'This Week'],
            ['last_week', 'Last Week'],
            ['30days', 'Last 30 Days'],
            ['custom', 'Custom'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setPreset(id)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              preset === id
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}

        {preset === 'custom' && (
          <>
            <input
              type="date"
              className="input w-40"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              className="input w-40"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
            />
          </>
        )}
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
        </div>
      )}

      {isError && (
        <div className="card border-red-200 bg-red-50 text-center text-red-700">
          Failed to load metrics.
        </div>
      )}

      {!isLoading && !isError && metrics && (
        <>
          {/* Stat cards */}
          <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {statCards.map((stat) => (
              <div key={stat.label} className="stat-card">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {stat.label}
                </span>
                <span className={`text-2xl font-bold ${stat.color}`}>{stat.value}</span>
              </div>
            ))}
          </div>

          {/* Category breakdown */}
          <div className="card">
            <h3 className="mb-4 text-sm font-semibold text-gray-900 uppercase tracking-wide">
              By Category
            </h3>
            <div className="flex flex-col gap-3">
              {Object.entries(metrics.by_category)
                .sort(([, a], [, b]) => b.count - a.count)
                .map(([category, data]) => {
                  const pct = (data.count / maxCategoryCount) * 100;
                  const autoPct = data.count > 0 ? (data.auto / data.count) * 100 : 0;
                  const barColor = CATEGORY_COLORS[category] ?? 'bg-gray-400';
                  return (
                    <div key={category}>
                      <div className="mb-1 flex items-center justify-between text-sm">
                        <span className="font-medium text-gray-700">
                          {category.replace('_', ' ')}
                        </span>
                        <span className="text-xs text-gray-500">
                          {data.count} total &middot; {data.auto} auto &middot; {data.manual} manual
                        </span>
                      </div>
                      <div className="relative h-6 w-full overflow-hidden rounded-full bg-gray-100">
                        <div
                          className={`absolute inset-y-0 left-0 rounded-full ${barColor} transition-all duration-500`}
                          style={{ width: `${pct}%` }}
                        />
                        {/* Auto-handled portion overlay */}
                        <div
                          className={`absolute inset-y-0 left-0 rounded-full ${barColor} opacity-60 transition-all duration-500`}
                          style={{ width: `${(pct * autoPct) / 100}%` }}
                        />
                        <span className="absolute inset-0 flex items-center px-3 text-xs font-medium text-white drop-shadow-sm">
                          {autoPct.toFixed(0)}% auto
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
